import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowRight, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function AcceptInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setUser, lang, setLang } = useAuth();
    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/auth/invitation/${token}`);
                setInvitation(data);
            } catch (e) {
                setError(errMsg(e, t(lang, "invalidInvite")));
            } finally {
                setLoading(false);
            }
        })();
    }, [token, lang]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError(lang === "tr" ? "Şifre en az 6 karakter olmalı" : lang === "de" ? "Passwort muss mindestens 6 Zeichen lang sein" : "Password must be at least 6 chars");
            return;
        }
        if (password !== confirm) {
            setError(lang === "tr" ? "Şifreler uyuşmuyor" : lang === "de" ? "Passwörter stimmen nicht überein" : "Passwords do not match");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/accept-invitation", { token, password });
            if (data.token) localStorage.setItem("taskflow_token", data.token);
            setUser(data.user);
            navigate("/");
        } catch (e) {
            setError(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen auth-backdrop flex items-center justify-center p-6" data-testid="accept-invite-page">
            <div className="w-full max-w-sm">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-2.5">
                        <div className="h-7 px-1.5 rounded-md bg-black flex items-center justify-center">
                            <img src="/brand/dutacs-logo-200.png" alt="DuTACS" className="h-4 w-auto" draggable="false" />
                        </div>
                        <span className="font-display font-bold tracking-tight">MyTACS</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                        {["tr", "en", "de"].map((l) => (
                            <button key={l} onClick={() => setLang(l)} data-testid={`accept-lang-${l}`}
                                className={`px-2 py-1 rounded ${lang === l ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
                                {l.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                    </div>
                )}

                {!loading && error && !invitation && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="invite-error">
                        <p className="text-sm text-red-700 font-medium">{error}</p>
                        <button onClick={() => navigate("/login")} className="mt-3 text-sm text-neutral-700 underline">
                            ← {t(lang, "login")}
                        </button>
                    </div>
                )}

                {invitation && (
                    <>
                        <div className="flex items-center gap-2 mb-2 text-emerald-700">
                            <CheckCircle2 className="w-4 h-4" />
                            <p className="text-xs uppercase tracking-[0.18em] font-semibold">{t(lang, "invite")}</p>
                        </div>
                        <h2 className="font-display text-3xl font-bold tracking-tight">{t(lang, "welcomeAccept")}</h2>
                        <div className="mt-4 p-3 rounded-md border border-neutral-200 bg-neutral-50 text-xs">
                            <p><span className="text-neutral-500">{t(lang, "name")}:</span> <span className="font-medium">{invitation.name}</span></p>
                            <p><span className="text-neutral-500">{t(lang, "email")}:</span> <span className="font-medium">{invitation.email}</span></p>
                            <p><span className="text-neutral-500">{t(lang, "role")}:</span> <span className="font-medium uppercase">{invitation.role}</span></p>
                        </div>

                        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="accept-form">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-700">{t(lang, "password")}</Label>
                                <div className="relative mt-1.5">
                                    <Input
                                        type={showPw ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="accept-password-input"
                                        className="h-11 pr-9"
                                        autoFocus
                                    />
                                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500">
                                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-700">
                                    {lang === "tr" ? "Şifre Tekrar" : lang === "de" ? "Passwort wiederholen" : "Confirm Password"}
                                </Label>
                                <Input
                                    type={showPw ? "text" : "password"}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="accept-password-confirm"
                                    className="h-11 mt-1.5"
                                />
                            </div>
                            {error && (
                                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="accept-error">
                                    {error}
                                </div>
                            )}
                            <Button type="submit" disabled={busy} data-testid="accept-submit" className="w-full h-11 bg-neutral-900 hover:bg-neutral-800">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                    <>{t(lang, "setPassword")}<ArrowRight className="w-4 h-4 ml-1" /></>
                                )}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
